import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useCurrentUserQuery } from "@base/model/hooks";
import { useRouterState } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import {
  captureScrollAnchor,
  createDailyLogSearchReturnState,
  loadNavigationEntry,
  saveNavigationEntry,
  toListSearchKey,
} from "../model/dailyLogSearchNavigation";
import type { DailyLogSearchItem } from "../model/dailyLogSearchSchema";
import {
  type DailyLogSearchConditions,
  type DailyLogSearchRouteInput,
  type DailyLogSearchRouteParams,
  FILTER_IDS_MAX,
  KEYWORD_MAX_CODE_POINTS,
  type SearchConditionIssue,
  hasAppliedConditions,
  parseSearchConditions,
  toRouteSearch,
} from "../model/dailyLogSearchState";
import { type SearchApplyTrigger, trackSearchApplied } from "../model/dailyLogSearchTelemetry";
import { useDailyLogSearch } from "../model/useDailyLogSearch";
import {
  type RestoreSettledResult,
  useDailyLogSearchScrollRestoration,
} from "../model/useDailyLogSearchScrollRestoration";
import { DailyLogSearchDrafts } from "./DailyLogSearchDrafts";
import { DailyLogSearchFilters } from "./DailyLogSearchFilters";
import { DailyLogSearchKeywordInput } from "./DailyLogSearchKeywordInput";
import { DailyLogSearchList, type ResultOpenHandler } from "./DailyLogSearchList";

/** 목록 복원 모듈이 소유하는 유일한 스크롤 컨테이너 */
export const DAILY_LOG_SEARCH_SCROLL_ID = "daily-log-search-scroll";

interface DailyLogSearchPanelProps {
  projectId: number;
  /** URL에 적용된 검색 조건 (route가 검증한 원본 문자열) */
  routeParams: DailyLogSearchRouteParams;
  /** 적용 조건을 URL에 반영한다. history entry를 늘리지 않도록 replace로 갱신한다 */
  onRouteParamsChange: (params: DailyLogSearchRouteInput) => void;
}

export function DailyLogSearchPanel({ projectId, routeParams, onRouteParamsChange }: DailyLogSearchPanelProps) {
  const { conditions, issues } = useMemo(() => parseSearchConditions(routeParams), [routeParams]);
  const hasIssues = issues.length > 0;
  const listSearchKey = useMemo(() => toListSearchKey(routeParams), [routeParams]);

  const { data: currentUser } = useCurrentUserQuery();
  const userId = currentUser?.user.id;
  const historyKey = useRouterState({ select: (state) => state.location.state.__TSR_key });
  const historyIndex = useRouterState({ select: (state) => state.location.state.__TSR_index });

  // 이 history entry로 돌아왔을 때만 저장된 위치를 적용한다. 조건이 다르면 적용하지 않는다.
  // 조건이 바뀌어도 다시 읽지 않는다 — 마운트한 entry 기준으로 한 번만 복원한다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: listSearchKey 변경에 반응하지 않는다
  const navigationEntry = useMemo(() => {
    if (userId === undefined || !historyKey) return null;
    const entry = loadNavigationEntry(userId, projectId, historyKey);
    return entry?.listSearchKey === listSearchKey ? entry : null;
  }, [userId, projectId, historyKey]);

  const containerRef = useRef<HTMLDivElement>(null);
  const preserveAnchorRef = useRef<() => void>();
  const applyAnchorAfterSwapRef = useRef<() => void>();
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);

  const search = useDailyLogSearch({
    projectId,
    conditions,
    enabled: !hasIssues,
    restorePageCount: navigationEntry?.loadedPageCount ?? null,
    onBeforeChainSwap: () => preserveAnchorRef.current?.(),
    onAfterChainSwap: () => applyAnchorAfterSwapRef.current?.(),
  });

  const handleRestoreSettled = (result: RestoreSettledResult) => {
    const fallbackReason = getRestoreFallbackReason(result);
    if (fallbackReason !== null && fallbackReason !== "fetch_failed") {
      setRestoreNotice("Some daily logs changed since you left, so we kept you close to where you were.");
    }
    EventLogger.track(CLIENT_EVENT.DAILY_LOG_SEARCH_RESTORED, {
      project_id: String(projectId),
      cache_hit: result.cacheHit,
      restored: result.restored,
      fallback_reason: fallbackReason,
      loaded_page_count: navigationEntry?.loadedPageCount ?? 0,
    });
  };

  const { preserveAnchorForNextRender, applyAnchorAfterSwap, abandonInitialRestore } =
    useDailyLogSearchScrollRestoration({
      containerRef,
      entry: navigationEntry,
      status: search.status,
      items: search.items,
      conditionsKey: listSearchKey,
      onSettled: handleRestoreSettled,
    });
  preserveAnchorRef.current = preserveAnchorForNextRender;
  applyAnchorAfterSwapRef.current = applyAnchorAfterSwap;

  const applyConditions = (next: DailyLogSearchConditions, trigger: SearchApplyTrigger) => {
    trackSearchApplied(projectId, next, trigger);
    setRestoreNotice(null);
    onRouteParamsChange(toRouteSearch(next));
  };

  const resetAll = () => applyConditions(parseSearchConditions({}).conditions, "reset");

  const returnState = createDailyLogSearchReturnState(projectId, routeParams, historyIndex ?? 0);

  /** 같은 탭에서 이 목록을 떠나 상세·편집으로 갈 때 돌아올 위치를 저장한다 */
  const saveListPosition = (item: DailyLogSearchItem) => {
    const container = containerRef.current;
    if (userId === undefined || !historyKey || !container) return;
    saveNavigationEntry({
      version: 1,
      userId,
      projectId,
      listSearchKey,
      historyKey,
      selectedLogId: item.id,
      loadedPageCount: search.loadedPageCount,
      updatedAt: Date.now(),
      ...captureScrollAnchor(container),
    });
  };

  const handleResultOpen: ResultOpenHandler = (item: DailyLogSearchItem, position: number, openMethod) => {
    EventLogger.click(CLIENT_EVENT.DAILY_LOG_SEARCH_RESULT_OPENED, {
      project_id: String(projectId),
      log_id: String(item.id),
      row_position: position,
      request_id: search.getDisplayedRequestId(),
      open_method: openMethod,
    });
    // 새 탭은 이 목록 entry로 돌아오지 않으므로 위치를 저장하지 않는다.
    if (openMethod === "same_tab") saveListPosition(item);
  };

  return (
    <div className="flex flex-col h-full min-h-0 md:overflow-hidden md:rounded-8 md:border md:border-gray-200 md:bg-white">
      <div className="flex flex-col gap-2 shrink-0 md:gap-3 md:p-4 md:pb-3 md:border-b md:border-gray-200">
        {/* 검색창 오른쪽 같은 줄에 필터(모바일 "Filters", PC 필터 3개)를 두고, 적용 조건 칩은 다음 줄로 내린다 */}
        <div className="flex flex-row flex-wrap gap-2 items-start md:gap-x-3 md:gap-y-2">
          <div className="flex-1 min-w-0 md:min-w-[240px] md:max-w-[420px]">
            <DailyLogSearchKeywordInput
              appliedKeyword={conditions.keyword}
              onApply={(keyword, trigger) => applyConditions({ ...conditions, keyword }, trigger)}
            />
          </div>
          <DailyLogSearchFilters projectId={projectId} conditions={conditions} onApply={applyConditions} />
        </div>
        {hasIssues && (
          <InvalidConditionsNotice
            issues={issues}
            onRemoveInvalid={() => applyConditions(conditions, "fix_invalid")}
            onResetAll={resetAll}
          />
        )}
      </div>

      {/* 하단 여백: 모바일의 새 일지 작성(+) 버튼이 마지막 행을 가리지 않게 한다. PC는 header 버튼을 쓴다 */}
      <div
        ref={containerRef}
        id={DAILY_LOG_SEARCH_SCROLL_ID}
        className="overflow-y-auto flex-1 pb-20 mt-2 min-h-0 md:px-4 md:pt-3 md:pb-4 md:mt-0"
      >
        <DailyLogSearchDrafts projectId={projectId} />
        {restoreNotice && (
          <output className="block p-3 my-2 text-body4 text-gray-800 bg-gray-100 rounded-8">{restoreNotice}</output>
        )}
        <DailyLogSearchList
          projectId={projectId}
          search={search}
          hasKeyword={conditions.keyword !== ""}
          hasConditions={hasAppliedConditions(conditions)}
          onResetAll={resetAll}
          linkState={returnState}
          onResultOpen={handleResultOpen}
          onEditOpen={saveListPosition}
          onAbandonRestore={() => {
            abandonInitialRestore();
            search.abandonRestore();
          }}
        />
      </div>
    </div>
  );
}

function getRestoreFallbackReason(
  result: RestoreSettledResult,
): "nearby" | "date" | "scroll_top" | "no_rows" | "fetch_failed" | null {
  if (!result.restored) return result.reason;
  switch (result.result.method) {
    case "anchor":
      return null;
    case "nearby":
      return "nearby";
    case "date":
      return "date";
    case "scroll-top":
      return "scroll_top";
  }
}

interface InvalidConditionsNoticeProps {
  issues: SearchConditionIssue[];
  onRemoveInvalid: () => void;
  onResetAll: () => void;
}

/** 잘못된 URL 조건을 조용히 무시하거나 다른 값으로 바꾸지 않고, 사용자가 제거하도록 안내한다 */
function InvalidConditionsNotice({ issues, onRemoveInvalid, onResetAll }: InvalidConditionsNoticeProps) {
  return (
    <div className="flex flex-col gap-2 p-3 border border-red-200 bg-red-10 rounded-8" role="alert">
      <p className="text-label2 text-red-700">Some search conditions in this link are invalid.</p>
      <ul className="pl-4 list-disc text-body4 text-gray-800">
        {issues.map((issue) => (
          <li key={issue}>{getIssueMessage(issue)}</li>
        ))}
      </ul>
      <div className="flex gap-3">
        <button type="button" className="underline text-label3 text-blue-600" onClick={onRemoveInvalid}>
          Remove invalid conditions
        </button>
        <button type="button" className="underline text-label3 text-blue-600" onClick={onResetAll}>
          Reset all
        </button>
      </div>
    </div>
  );
}

function getIssueMessage(issue: SearchConditionIssue): string {
  switch (issue) {
    case "keyword-too-long":
      return `The keyword is longer than ${KEYWORD_MAX_CODE_POINTS} characters.`;
    case "invalid-start-date":
      return "The start date is not a valid date.";
    case "invalid-end-date":
      return "The end date is not a valid date.";
    case "reversed-date-range":
      return "The start date is after the end date.";
    case "invalid-contractors":
      return "The contractor filter is not valid.";
    case "too-many-contractors":
      return `More than ${FILTER_IDS_MAX} contractors are selected.`;
    case "invalid-tags":
      return "The status filter is not valid.";
    case "too-many-tags":
      return `More than ${FILTER_IDS_MAX} statuses are selected.`;
  }
}
